import { useAuth } from '../../../hooks/useAuth'
import {
  CalendarIcon,
  UserGroupIcon,
  ChartBarIcon,
  DocumentTextIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import type { IconComponent } from '../../../types/icon'

interface QuickAction {
  name: string
  icon: IconComponent
  onClick: () => void
  variant?: 'primary' | 'secondary'
  disabled?: boolean
}

interface QuickActionsProps {
  onCreateEvent?: () => void
  onAddAthlete?: () => void
  onViewAnalytics?: () => void
  onGenerateReport?: () => void
  onViewResults?: () => void
}

export default function QuickActions({
  onCreateEvent,
  onAddAthlete,
  onViewAnalytics,
  onGenerateReport,
  onViewResults,
}: QuickActionsProps) {
  const { user } = useAuth()

  // Define role-specific actions
  const getRoleActions = (): QuickAction[] => {
    const actions: QuickAction[] = []

    switch (user?.role) {
      case 'ADMIN':
      case 'EVENT_ORGANIZER':
        actions.push(
          {
            name: 'Create Event',
            icon: CalendarIcon,
            onClick: onCreateEvent || (() => (window.location.href = '/events/create')),
            variant: 'primary',
          },
          {
            name: 'Add Athletes',
            icon: UserGroupIcon,
            onClick: onAddAthlete || (() => (window.location.href = '/athletes/add')),
          },
          {
            name: 'View Analytics',
            icon: ChartBarIcon,
            onClick: onViewAnalytics || (() => (window.location.href = '/analytics')),
          },
          {
            name: 'Generate Report',
            icon: DocumentTextIcon,
            onClick: onGenerateReport || (() => (window.location.href = '/reports')),
          }
        )
        break

      case 'COACH':
        actions.push(
          {
            name: 'View Athletes',
            icon: UserGroupIcon,
            onClick: () => (window.location.href = '/athletes'),
            variant: 'primary',
          },
          {
            name: 'View Results',
            icon: ChartBarIcon,
            onClick: onViewResults || (() => (window.location.href = '/results')),
          },
          {
            name: 'Team Analytics',
            icon: DocumentTextIcon,
            onClick: onViewAnalytics || (() => (window.location.href = '/analytics')),
          }
        )
        break

      case 'ATHLETE':
        actions.push(
          {
            name: 'View My Results',
            icon: ChartBarIcon,
            onClick: onViewResults || (() => (window.location.href = '/results')),
            variant: 'primary',
          },
          {
            name: 'My Performance',
            icon: DocumentTextIcon,
            onClick: onViewAnalytics || (() => (window.location.href = '/analytics')),
          }
        )
        break

      case 'PARENT':
        actions.push(
          {
            name: "Child's Results",
            icon: EyeIcon,
            onClick: onViewResults || (() => (window.location.href = '/results')),
            variant: 'primary',
          },
          {
            name: "Child's Progress",
            icon: ChartBarIcon,
            onClick: onViewAnalytics || (() => (window.location.href = '/analytics')),
          }
        )
        break

      case 'SCOUT':
        actions.push(
          {
            name: 'Search Athletes',
            icon: UserGroupIcon,
            onClick: () => (window.location.href = '/athletes'),
            variant: 'primary',
          },
          {
            name: 'View Analytics',
            icon: ChartBarIcon,
            onClick: onViewAnalytics || (() => (window.location.href = '/analytics')),
          },
          {
            name: 'Export Data',
            icon: DocumentTextIcon,
            onClick: onGenerateReport || (() => (window.location.href = '/reports')),
          }
        )
        break

      default:
        actions.push({
          name: 'View Dashboard',
          icon: EyeIcon,
          onClick: () => (window.location.href = '/'),
          variant: 'primary',
        })
    }

    return actions
  }

  const actions = getRoleActions()

  return (
    <div className="grid grid-cols-1 gap-4">
      {actions.map((action, index) => (
        <button
          key={index}
          onClick={action.onClick}
          disabled={action.disabled}
          className={`
            flex items-center justify-center px-4 py-3 rounded-md text-sm font-medium
            transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed
            ${
              action.variant === 'primary'
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          `}
        >
          <action.icon className="h-5 w-5 mr-2" />
          {action.name}
        </button>
      ))}
    </div>
  )
}
